// apps/radar/src/routes/index.tsx
// Version: 2026.4.24

import { createFileRoute } from '@tanstack/react-router';
import {
  EnginePageShell, GlassPanel, SectionLabel, MetricCard, EmptyState,
  LoadingShimmer, useCounter, C, F,
} from '../components/engine';
import { useDashboard } from '../lib/api/hooks';

export const Route = createFileRoute('/')({ component: DashboardPage });

const RADAR_BRAND  = 'FORGERADAR';
const RADAR_FOOTER = 'ForgeRadar · Edson Technologies · forgeradar.app';

function DashboardPage() {
  const { data, loading, error, refetch } = useDashboard();

  // Animate counters off the live data, falling back to 0 while loading.
  const hot   = useCounter(data?.hotLeads     ?? 0, 900, 100);
  const warm  = useCounter(data?.warmLeads    ?? 0, 900, 200);
  const cold  = useCounter(data?.coldLeads    ?? 0, 900, 300);
  const live  = useCounter(data?.sourcesActive ?? 0, 900, 400);

  return (
    <EnginePageShell
      title="Dashboard"
      subtitle="COMMAND CENTER"
      icon="📡"
      accentColor={C.cyan}
      brand={RADAR_BRAND}
      footer={RADAR_FOOTER}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16, marginBottom: 24,
      }}>
        <MetricCard label="Hot Leads"     value={hot}  color={C.coral}  icon="🔥" subtitle="Score ≥ 80 — push to CRM immediately" />
        <MetricCard label="Warm Leads"    value={warm} color={C.amber}  icon="🌤" subtitle="Score 60–79 — daily digest" />
        <MetricCard label="Cold Leads"    value={cold} color={C.blue}   icon="🧊" subtitle="Score < 60 — searchable, not pushed" />
        <MetricCard
          label="Sources Live"
          value={live}
          color={C.violet}
          icon="📡"
          subtitle={data ? `of ${data.sourcesTotal} configured sources` : 'of 8 Phase 1 sources'}
        />
      </div>

      <GlassPanel accent={C.cyan}>
        <SectionLabel color={C.cyan}>Recent Activity</SectionLabel>

        {error && (
          <ErrorBlock label="Dashboard" message={error.message} onRetry={refetch} />
        )}

        {!error && loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 0' }}>
            <LoadingShimmer height={20} />
            <LoadingShimmer height={20} />
            <LoadingShimmer height={20} />
          </div>
        )}

        {!error && !loading && (data?.recentActivity?.length ?? 0) === 0 && (
          <EmptyState
            icon="📡"
            title="No artifacts yet"
            subtitle="Sources begin crawling once the pipeline workers ship in Sprint 4. Until then, the inbox stays empty by design."
          />
        )}

        {!error && !loading && (data?.recentActivity?.length ?? 0) > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {data!.recentActivity.map(row => (
              <div key={row.leadId} style={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr 100px 110px',
                gap: 12, alignItems: 'center',
                padding: '10px 4px',
                borderBottom: `1px solid ${C.faint}`,
                fontSize: 13,
              }}>
                <span style={{ fontFamily: F.mono, fontSize: 11, color: tierColor(row.tier) }}>
                  {row.tier.toUpperCase()}
                </span>
                <span style={{ color: C.text }}>{row.title}</span>
                <span style={{ color: C.muted, fontFamily: F.mono, fontSize: 11 }}>{row.sourceCode}</span>
                <span style={{ color: C.dim, fontFamily: F.mono, fontSize: 11 }}>
                  {new Date(row.capturedAt).toISOString().slice(0, 16).replace('T', ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>
    </EnginePageShell>
  );
}

function tierColor(tier: string): string {
  return tier === 'hot' ? C.coral : tier === 'warm' ? C.amber : C.blue;
}

function ErrorBlock({ label, message, onRetry }: { label: string; message: string; onRetry: () => void }) {
  return (
    <div style={{ padding: '20px 4px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 24 }}>⚠</span>
      <div style={{ flex: 1 }}>
        <div style={{ color: C.coral, fontWeight: 600, marginBottom: 4 }}>{label} unavailable</div>
        <div style={{ color: C.muted, fontSize: 13 }}>{message}</div>
      </div>
      <button
        onClick={onRetry}
        style={{
          background: `${C.coral}15`, border: `1px solid ${C.coral}40`,
          color: C.coral, fontFamily: F.mono, fontSize: 11,
          letterSpacing: '0.08em', padding: '6px 14px', borderRadius: 6,
          cursor: 'pointer',
        }}
      >
        RETRY
      </button>
    </div>
  );
}
